import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    alpha,
} from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import Popup from "../../components/Popup";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import { getApiBaseUrl } from "../../config/api";
import { getSchoolIdForUser } from "../../utils/userContext";
import {
    allowedReportPeriods,
    defaultReportPeriod,
    submissionMatchesReportPeriod,
} from "../../utils/reportPeriodUtils";
import { useHomeroomTeacherTerm } from "../../hooks/useHomeroomTeacherTerm";
import {
    dedupeReadinessSubjects,
    dedupeSubjectQueue,
    readinessStatusLabel,
} from "../../utils/subjectNameUtils";
import PrimaryTermPromotionReview from "../../components/exam/PrimaryTermPromotionReview";

const TERM_OPTIONS = ["Term 1", "Term 2", "Term 3"];

function sortSubmissionItems(items) {
    if (!Array.isArray(items)) return [];
    return [...items].sort((a, b) => {
        const rollA = Number(a.student?.rollNum ?? a.rollNum ?? 0);
        const rollB = Number(b.student?.rollNum ?? b.rollNum ?? 0);
        return rollA - rollB;
    });
}

const TeacherFormTeacherResults = () => {
    const { currentUser } = useSelector((s) => s.user);
    const teacherId = currentUser?._id || currentUser?.id;
    const homeroomTerm = useHomeroomTeacherTerm();
    const schoolId = getSchoolIdForUser(currentUser);

    const [tab, setTab] = useState(0);
    const [classes, setClasses] = useState([]);
    const [classId, setClassId] = useState("");
    const [term, setTerm] = useState("Term 1");
    const [academicYear, setAcademicYear] = useState(String(new Date().getFullYear()));
    const [reportPeriod, setReportPeriod] = useState("End of term");

    const [subjectQueue, setSubjectQueue] = useState([]);
    const [readiness, setReadiness] = useState(null);
    const [bundles, setBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [detailSubmission, setDetailSubmission] = useState(null);

    const activeClass = useMemo(
        () => classes.find((c) => (c._id || c.id) === classId),
        [classes, classId],
    );
    const periodOptions = useMemo(
        () => allowedReportPeriods(activeClass?.educationLevel),
        [activeClass?.educationLevel],
    );

    const loadClasses = useCallback(async () => {
        if (!teacherId) return;
        const { data } = await axios.get(`${getApiBaseUrl()}/Enrollment/teacher/${teacherId}/classes`);
        const list = Array.isArray(data) ? data : [];
        setClasses(list);
        if (list.length === 1) setClassId(list[0]._id || list[0].id);
    }, [teacherId]);

    const loadAll = useCallback(async () => {
        if (!teacherId) return;
        setLoading(true);
        try {
            const [subRes, bundleRes] = await Promise.all([
                axios.get(`${getApiBaseUrl()}/ClassResults/form-teacher/${teacherId}/subjects`),
                axios.get(`${getApiBaseUrl()}/ClassResults/form-teacher/${teacherId}/bundles`),
            ]);
            setSubjectQueue(Array.isArray(subRes.data) ? subRes.data : []);
            setBundles(Array.isArray(bundleRes.data) ? bundleRes.data : []);

            if (classId) {
                const { data: ready } = await axios.get(`${getApiBaseUrl()}/ClassResults/readiness`, {
                    params: { classId, teacherId, term, academicYear, reportPeriod },
                });
                setReadiness(ready);
            } else {
                setReadiness(null);
            }
        } catch (err) {
            console.error("loadFormTeacherResults", err);
            setSubjectQueue([]);
            setBundles([]);
        } finally {
            setLoading(false);
        }
    }, [teacherId, classId, term, academicYear, reportPeriod]);

    useEffect(() => {
        loadClasses().catch(() => setClasses([]));
    }, [loadClasses]);

    useEffect(() => {
        if (activeClass?.educationLevel) {
            setReportPeriod(defaultReportPeriod(activeClass.educationLevel));
        }
    }, [activeClass?.educationLevel, classId]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const handleFormApprove = async (id) => {
        setActing(true);
        try {
            await axios.put(`${getApiBaseUrl()}/ClassResults/subject/${id}/form-approve`, { teacherId });
            setMessage("Subject marks accepted.");
            setPopupSeverity("success");
            setShowPopup(true);
            loadAll();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not approve");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setActing(false);
        }
    };

    const handleSubmitToDirector = async (submissionIds = null) => {
        if (!classId) return;
        setActing(true);
        try {
            const payload = {
                teacherId,
                schoolId,
                classId,
                term,
                academicYear,
                reportPeriod,
            };
            if (Array.isArray(submissionIds) && submissionIds.length) {
                payload.submissionIds = submissionIds;
            }
            const { data } = await axios.post(`${getApiBaseUrl()}/ClassResults/submit`, payload);
            setMessage(data?.message || "Class results sent to director for approval.");
            setPopupSeverity("success");
            setShowPopup(true);
            loadAll();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not submit class results");
            setPopupSeverity("error");
            setShowPopup(true);
        } finally {
            setActing(false);
        }
    };

    const displaySubjects = useMemo(
        () => dedupeReadinessSubjects(readiness?.subjects || []),
        [readiness?.subjects],
    );

    if (!loading && !classes.length) {
        return (
            <Container maxWidth="md" sx={{ py: 3 }}>
                <Alert severity="info">
                    You are not a {homeroomTerm.theRole} for any class. The director must assign you as{" "}
                    {homeroomTerm.theRole} on a class before you can review subject marks and send report cards.
                </Alert>
            </Container>
        );
    }

    const matchesClass = (s) =>
        !classId || s.classId === classId || s.class?._id === classId || s.class?.id === classId;

    const matchesFilters = (s) => {
        const sameTerm = !term || s.term === term;
        const sameYear = !academicYear || String(s.academicYear) === String(academicYear);
        const samePeriod = submissionMatchesReportPeriod(s, reportPeriod);
        return sameTerm && sameYear && samePeriod;
    };

    const queueForClass = subjectQueue.filter(matchesClass);

    const queueFiltered = queueForClass.filter(
        (s) => s.status === "Submitted" || matchesFilters(s),
    );

    const queueSorted = [...dedupeSubjectQueue(queueFiltered)].sort((a, b) => {
        if (a.status === "Submitted" && b.status !== "Submitted") return -1;
        if (b.status === "Submitted" && a.status !== "Submitted") return 1;
        return String(a.subject?.subName || "").localeCompare(String(b.subject?.subName || ""));
    });

    const sendableSubjects = displaySubjects.filter(
        (sub) => sub.status === "FormApproved" && sub.submissionId,
    );

    const pendingCount = queueForClass.filter((s) => s.status === "Submitted").length;
    const approvedInQueue = queueForClass.filter(
        (s) => s.status === "FormApproved" && matchesFilters(s),
    ).length;

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Typography variant="h5" fontWeight={800} gutterBottom>
                {homeroomTerm.roleTitleCaps} — class results &amp; report cards
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review other teachers&apos; marks and accept them. Your own subjects in this class skip review — submit
                marks under Submit subject marks, then send to the director here. If you also teach another class,
                submit those marks to that class&apos;s form teacher instead.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Class</InputLabel>
                            <Select label="Class" value={classId} onChange={(e) => setClassId(e.target.value)}>
                                {classes.map((c) => {
                                    const id = c._id || c.id;
                                    return (
                                        <MenuItem key={id} value={id}>
                                            {c.sclassName}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Term</InputLabel>
                            <Select label="Term" value={term} onChange={(e) => setTerm(e.target.value)}>
                                {TERM_OPTIONS.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Year</InputLabel>
                            <Select
                                label="Year"
                                value={academicYear}
                                onChange={(e) => setAcademicYear(e.target.value)}
                            >
                                {[0, 1].map((off) => {
                                    const y = String(new Date().getFullYear() - off);
                                    return (
                                        <MenuItem key={y} value={y}>
                                            {y}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Report period</InputLabel>
                            <Select
                                label="Report period"
                                value={reportPeriod}
                                onChange={(e) => setReportPeriod(e.target.value)}
                            >
                                {periodOptions.map((p) => (
                                    <MenuItem key={p} value={p}>
                                        {p}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                {readiness ? (
                    <Alert severity={readiness.ready ? "success" : "warning"} sx={{ mt: 2 }}>
                        Accepted for {reportPeriod}: {readiness.approvedCount || 0}
                        {readiness.withDirectorCount > 0
                            ? ` · ${readiness.withDirectorCount} already with director`
                            : ""}
                        {readiness.pendingCount > 0
                            ? ` · ${readiness.pendingCount} awaiting your review`
                            : ""}
                        {readiness.sendableCount > 0 && !readiness.ready
                            ? ` · ${readiness.sendableCount} ready to send`
                            : ""}
                        .{" "}
                        {readiness.directorBundleOpen && readiness.sendableCount > 0
                            ? "Send more accepted subjects — they are added to the same package with the director."
                            : readiness.ready
                              ? "You can send the full class to the director, or send subjects one at a time below."
                              : readiness.pendingCount > 0
                                ? "Accept each subject on the Subject marks tab first, or send accepted subjects individually."
                                : readiness.sendableCount > 0
                                  ? "Send accepted subjects to the director below — you do not need to wait for every subject."
                                  : "Subject teachers must submit marks for this term and report period."}
                    </Alert>
                ) : null}
                {readiness?.outOfFilterCount > 0 ? (
                    <Alert
                        severity="info"
                        sx={{ mt: 2 }}
                        action={
                            readiness.outOfFilter[0]?.term ? (
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => {
                                        const row = readiness.outOfFilter[0];
                                        if (row.term) setTerm(row.term);
                                        if (row.academicYear) setAcademicYear(String(row.academicYear));
                                    }}
                                >
                                    Match filters
                                </Button>
                            ) : null
                        }
                    >
                        {readiness.outOfFilterCount} accepted submission(s) use a different term or year than
                        selected above, for example{" "}
                        {readiness.outOfFilter
                            .slice(0, 3)
                            .map((row) => `${row.subName} (${[row.term, row.academicYear].filter(Boolean).join(" ")})`)
                            .join(", ")}
                        {readiness.outOfFilterCount > 3 ? "…" : ""}. Adjust the filters or click Match filters.
                    </Alert>
                ) : null}
            </Paper>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab
                    label={`Subject marks (${pendingCount} pending${approvedInQueue ? `, ${approvedInQueue} accepted` : ""})`}
                />
                <Tab label="Send to director" />
                <Tab label="Report cards" />
            </Tabs>

            {tab === 0 && pendingCount > 0 && queueSorted.filter((s) => s.status === "Submitted").length === 0 ? (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    {readiness?.pendingCount || pendingCount} submission(s) are waiting, but none match the selected
                    term, year, and report period. Try another report period (e.g. Continuous assessment) or check filters above.
                </Alert>
            ) : null}

            {tab === 0 ? (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <StyledTableRow>
                                <StyledTableCell>Subject</StyledTableCell>
                                <StyledTableCell>Teacher</StyledTableCell>
                                <StyledTableCell>Term / period</StyledTableCell>
                                <StyledTableCell>Students</StyledTableCell>
                                <StyledTableCell>Status</StyledTableCell>
                                <StyledTableCell align="right">Actions</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={6} align="center">
                                        <CircularProgress size={24} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : null}
                            {!loading && queueSorted.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={6} align="center">
                                        No subject submissions for this class and filters.
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : null}
                            {queueSorted.map((row) => {
                                const outsideFilters =
                                    row.status === "Submitted" && !matchesFilters(row);
                                const id = row._id || row.id;
                                const rowTeacherId =
                                    row.teacherId || row.teacher?._id || row.teacher?.id;
                                const isOwnSubmission = String(rowTeacherId) === String(teacherId);
                                const itemCount = Array.isArray(row.items) ? row.items.length : 0;
                                const termLabel = [row.term, row.academicYear].filter(Boolean).join(" ") || "—";
                                const periodLabel = row.reportPeriod || row.examLabel || "—";
                                return (
                                    <StyledTableRow key={id}>
                                        <StyledTableCell>{row.subject?.subName}</StyledTableCell>
                                        <StyledTableCell>
                                            {row.teacher?.name}
                                            {isOwnSubmission ? (
                                                <Chip
                                                    size="small"
                                                    label="Your subject"
                                                    color="info"
                                                    variant="outlined"
                                                    sx={{ ml: 1 }}
                                                />
                                            ) : null}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Typography variant="body2">{periodLabel}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {termLabel}
                                            </Typography>
                                            {outsideFilters ? (
                                                <Chip
                                                    size="small"
                                                    color="warning"
                                                    label="Different term/period"
                                                    sx={{ mt: 0.5 }}
                                                />
                                            ) : null}
                                        </StyledTableCell>
                                        <StyledTableCell>{itemCount}</StyledTableCell>
                                        <StyledTableCell>
                                            <Chip size="small" label={row.status} />
                                        </StyledTableCell>
                                        <StyledTableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={itemCount === 0}
                                                    onClick={() => setDetailSubmission(row)}
                                                >
                                                    View grades
                                                </Button>
                                                {row.status === "Submitted" && !isOwnSubmission ? (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        disabled={acting}
                                                        onClick={() => handleFormApprove(id)}
                                                    >
                                                        Accept
                                                    </Button>
                                                ) : null}
                                                {row.status === "FormApproved" ? (
                                                    <>
                                                        <Chip
                                                            size="small"
                                                            color="success"
                                                            label={isOwnSubmission ? "Ready to send" : "Accepted"}
                                                        />
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="secondary"
                                                            disabled={acting}
                                                            startIcon={<SendOutlinedIcon />}
                                                            onClick={() => handleSubmitToDirector([id])}
                                                        >
                                                            Send to director
                                                        </Button>
                                                    </>
                                                ) : null}
                                            </Stack>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : null}

            {tab === 1 ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Submit class results to director
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The director will verify and publish results to students. You cannot edit subject marks after
                        this is approved. Match the same <strong>term</strong>, <strong>year</strong>, and{" "}
                        <strong>report period</strong> as the subject teacher used when entering marks.
                    </Typography>
                    {displaySubjects.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <StyledTableRow>
                                        <StyledTableCell>Subject</StyledTableCell>
                                        <StyledTableCell>Status</StyledTableCell>
                                        <StyledTableCell align="right">Action</StyledTableCell>
                                    </StyledTableRow>
                                </TableHead>
                                <TableBody>
                                    {displaySubjects.map((sub) => (
                                        <StyledTableRow key={sub.submissionId || sub.subjectId || sub.subName}>
                                            <StyledTableCell>{sub.subName}</StyledTableCell>
                                            <StyledTableCell>
                                                <Chip
                                                    size="small"
                                                    color={
                                                        sub.status === "WithDirector"
                                                            ? "info"
                                                            : sub.status === "FormApproved"
                                                              ? "success"
                                                              : sub.status === "AwaitingReview"
                                                                ? "warning"
                                                                : "default"
                                                    }
                                                    label={readinessStatusLabel(sub.status)}
                                                />
                                            </StyledTableCell>
                                            <StyledTableCell align="right">
                                                {sub.status === "FormApproved" && sub.submissionId ? (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        disabled={acting}
                                                        startIcon={<SendOutlinedIcon />}
                                                        onClick={() => handleSubmitToDirector([sub.submissionId])}
                                                    >
                                                        Send to director
                                                    </Button>
                                                ) : null}
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : null}
                    {!readiness?.ready && classId ? (
                        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                            {readiness?.pendingCount > 0
                                ? `Accept ${readiness.pendingCount} subject(s) on the Subject marks tab, or send ${readiness.sendableCount || 0} accepted subject(s) individually.`
                                : sendableSubjects.length > 0
                                  ? "Send accepted subjects individually using the buttons above."
                                  : "No accepted marks for this term and report period yet."}
                        </Alert>
                    ) : null}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                            variant="contained"
                            startIcon={<SendOutlinedIcon />}
                            disabled={acting || !readiness?.ready || !classId}
                            onClick={() => handleSubmitToDirector()}
                        >
                            Submit full class to director
                        </Button>
                        {sendableSubjects.length > 0 && !readiness?.ready ? (
                            <Button
                                variant="outlined"
                                startIcon={<SendOutlinedIcon />}
                                disabled={acting || !classId}
                                onClick={() =>
                                    handleSubmitToDirector(sendableSubjects.map((sub) => sub.submissionId))
                                }
                            >
                                Send {sendableSubjects.length} accepted subject
                                {sendableSubjects.length === 1 ? "" : "s"}
                            </Button>
                        ) : null}
                    </Stack>
                    {bundles.length > 0 ? (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Previous submissions
                            </Typography>
                            {bundles.map((b) => (
                                <Chip
                                    key={b._id || b.id}
                                    sx={{ mr: 1, mt: 1 }}
                                    label={`${b.class?.sclassName} · ${b.reportPeriod} · ${b.status}`}
                                />
                            ))}
                        </Box>
                    ) : null}
                </Paper>
            ) : null}

            {tab === 2 ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                        Report cards ({homeroomTerm.theRole})
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Open a student report card after the director has approved this class for{" "}
                        <strong>{reportPeriod}</strong>. Use the class roster to pick a pupil.
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            component={RouterLink}
                            to="/Teacher/class"
                            startIcon={<ArticleOutlinedIcon />}
                        >
                            Class roster → report cards
                        </Button>
                    </Stack>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        From a student profile, report cards use published marks for the selected term and report period
                        only.
                    </Alert>
                    {activeClass?.educationLevel === "Primary" && classId && schoolId ? (
                        <PrimaryTermPromotionReview
                            schoolId={schoolId}
                            classId={classId}
                            academicYear={academicYear}
                            teacherId={teacherId}
                        />
                    ) : null}
                </Paper>
            ) : null}

            <Dialog
                open={Boolean(detailSubmission)}
                onClose={() => setDetailSubmission(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {detailSubmission?.subject?.subName || "Subject"} — student marks
                </DialogTitle>
                <DialogContent dividers>
                    {detailSubmission ? (
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Teacher: <strong>{detailSubmission.teacher?.name || "—"}</strong>
                                    {" · "}
                                    Class:{" "}
                                    <strong>
                                        {detailSubmission.class?.sclassName || activeClass?.sclassName || "—"}
                                    </strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {detailSubmission.examLabel || "Assessment"}
                                    {detailSubmission.reportPeriod
                                        ? ` · ${detailSubmission.reportPeriod}`
                                        : ""}
                                    {detailSubmission.term || detailSubmission.academicYear
                                        ? ` · ${[detailSubmission.term, detailSubmission.academicYear].filter(Boolean).join(" ")}`
                                        : ""}
                                </Typography>
                            </Box>
                            {sortSubmissionItems(detailSubmission.items).length === 0 ? (
                                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                    No student marks in this submission. Ask the subject teacher to save marks and
                                    submit again.
                                </Alert>
                            ) : (
                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                    sx={{ borderRadius: 2, overflow: "hidden" }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                                }}
                                            >
                                                <TableCell sx={{ fontWeight: 700 }}>Roll</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                    Marks
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {sortSubmissionItems(detailSubmission.items).map((item) => {
                                                const key = item.id || item.studentId;
                                                return (
                                                    <TableRow key={key} hover>
                                                        <TableCell>
                                                            {item.student?.rollNum ?? "—"}
                                                        </TableCell>
                                                        <TableCell>{item.student?.name || "—"}</TableCell>
                                                        <TableCell align="right">
                                                            <Typography fontWeight={700}>
                                                                {item.marksObtained ?? "—"}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    {detailSubmission?.status === "Submitted" ? (
                        <Button
                            variant="contained"
                            disabled={acting}
                            onClick={() => {
                                const id = detailSubmission._id || detailSubmission.id;
                                handleFormApprove(id);
                                setDetailSubmission(null);
                            }}
                        >
                            Accept marks
                        </Button>
                    ) : null}
                    <Button onClick={() => setDetailSubmission(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Popup
                message={message}
                showPopup={showPopup}
                setShowPopup={setShowPopup}
                severity={popupSeverity}
            />
        </Container>
    );
};

export default TeacherFormTeacherResults;
